import './MobileLogsListPage.scss';

import { Roles } from '@dataquiver/stoneage-roles';
import {
  IonButton,
  IonButtons,
  IonChip,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonicSlides,
  IonLoading,
  IonModal,
  IonPage,
  IonPopover,
  IonRow,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { close, filter, warning } from 'ionicons/icons';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { BleService } from 'src/ble/BleService';
import { ChipType } from 'src/components/reusable-components/Chip';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { BleConnectionState } from 'src/redux/ble/constants';
import { selectBleRemoteLogFiles } from 'src/redux/ble/selectors';
import { selectLogLocalDataByFilename } from 'src/redux/log/selectors';
import { PermissionGuard } from 'src/utils/PermissionGuard';
import { useSocket, useSocketGet, useSocketList } from 'src/utils/socket';
import { FreeMode } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useBle } from '../../ble/useBle';
import { DqText } from '../../components/dq-text';
import { JobSyncSlide } from '../../components/job-sync-slide/JobSyncSlide';
import JobUploadForm from '../../components/job-upload/JobUploadForm';
import { LogsSearchParams } from '../../components/jobs-list/LogsTable';
import { MobileLogsBundlesTabs } from '../../components/MobileLogsBundlesTabs';
import MobileHeader from '../../components/reusable-components/MobileHeader';
import { ControllerMessages } from '../../intl/ControllerMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import { SelectOption } from '../../models/selectOption';
import { User } from '../../models/user';
import { appBarActions } from '../../redux/appBar/actions';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { FeatureGate } from '../../utils/feature-flags/FeatureFlags';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { useSocketSearch } from '../../utils/socket/useSocketSearch';
import FilterJobsModal, { FilterJobsModalState } from './FilterJobsModal';
import { JobsListInfinite, JobsListInfiniteItem } from './jobs-list-infinite/JobsListInfinite';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import { useHasBluetoothLE } from 'src/utils/helpers/platformHelpers';
import { useToast } from 'src/components/reusable-components/useToast';

export const FILTER_PROPERTY = {
  ORGANIZATION: 'organization',
  CONTROLLER: 'controller',
  END_DATE: 'endDate',
  START_DATE: 'startDate',
};
export interface FilterJobsState {
  controller: string | undefined;
  organization: string | undefined;
  startDate: number | undefined | null;
  endDate: number | undefined;
}

// Route based actions that can be performed on the page.
export enum MobileLogListPageActions {
  'Upload',
  'DeleteSuccessful',
}

const MobileLogsListPage: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [present] = useToast();
  const { isLimited } = useLimitedMode();
  const hasBluetooth = useHasBluetoothLE();

  const { state: locationState } = useLocation<{ action?: MobileLogListPageActions }>();
  const history = useHistory();

  /** The state of the currently logged in user. */
  const userData = useSelector(selectAuthUserInfo);
  /** The list of log files on the connected controller device. FIXME: Move to separate component. */
  const controllerLogFiles = useSelector(selectBleRemoteLogFiles);
  /** A dictionary of log files on the local device. */
  const localLogFiles = useSelector(selectLogLocalDataByFilename);

  const { bleState, connectedDevice, serialNumber } = useBle();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isUsersLoading, setUsersLoading] = useState(false);

  const [searchText, setSearchText] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const { item: userOrg } = useSocketGet(SocketContextPortal, 'organizations', userData?.org);
  const [filterOptionState, setFilterOptionState] = useState<FilterJobsState>({
    controller: undefined,

    // default to showing only logs from user's org
    organization: userData?.org,
    startDate: undefined,
    endDate: undefined,
  });

  const { request: portalRequest } = useSocket(SocketContextPortal, undefined);

  const supportedPageSize = 300;
  const searchRequest = useMemo(() => {
    const request: LogsSearchParams = {
      orgIds: filterOptionState.organization ? [filterOptionState.organization] : undefined,
      searchTerm: searchText,
      physicalSerials: filterOptionState.controller ? [filterOptionState.controller] : undefined,
      page: 0,
      pageSize: supportedPageSize,
      sort: ['createdAt', 'desc'],
    };
    return request;
  }, [filterOptionState.controller, filterOptionState.organization, searchText]);
  const { items: searchResults, fetched: isLogsFetched } = useSocketSearch(
    SocketContextSentinel,
    'logs',
    searchRequest,
  );

  const [formattedDates, setFormattedDates] = useState<{
    formattedStartDate: string | undefined;
    formattedEndDate: string | undefined;
  }>({ formattedStartDate: undefined, formattedEndDate: undefined });

  const applyFilter = useCallback(
    (filterObject: FilterJobsModalState) => {
      setFilterModalOpen(false);

      setFormattedDates({
        formattedStartDate: moment(filterObject.startDate).format('MMMM DD, YYYY'),
        formattedEndDate: moment(filterObject.endDate).format('MMMM DD, YYYY'),
      });

      setFilterOptionState({
        organization: filterObject.organization,
        controller: filterObject.controller,
        startDate: moment(filterObject.startDate)?.valueOf(),
        endDate: moment(filterObject.endDate)?.valueOf(),
      });
    },
    [setFilterModalOpen],
  );

  const filterApplied =
    filterOptionState.controller ||
    filterOptionState.organization ||
    filterOptionState.startDate ||
    filterOptionState.endDate;

  /** Formats the filter values based on their property key. */
  const formattedFilterValues = useCallback(
    (filterKey: string, filterValue: string) => {
      switch (filterKey) {
        case FILTER_PROPERTY.END_DATE:
          return `${formatMessage(JobMessages.endDate)} ${formattedDates.formattedEndDate}`;
        case FILTER_PROPERTY.START_DATE:
          return `${formatMessage(JobMessages.startDate)} ${formattedDates.formattedStartDate}`;
        case FILTER_PROPERTY.ORGANIZATION:
          // we default to filtering by the user's org, but they might not have any owned logs, i.e. all
          // logs are shared with them. If that's the case, the lookup into relations will fail, so we
          // short circuit here and return the user's org name.
          if (filterValue === userData?.org) {
            return userOrg?.orgName ?? formatMessage(GenericMessages.unknownPlaceholder);
          }

          return (
            searchResults?.relations?.organizations?.[filterValue]?.orgName ??
            formatMessage(GenericMessages.unknownPlaceholder)
          );
        case FILTER_PROPERTY.CONTROLLER:
          return `${formatMessage(JobMessages.controller)} ${filterValue}`;
        default:
          return formatMessage(GenericMessages.unknownPlaceholder);
      }
    },
    [
      userData?.org,
      userOrg,
      formatMessage,
      formattedDates.formattedEndDate,
      formattedDates.formattedStartDate,
      searchResults?.relations?.organizations,
    ],
  );

  const jobsListMessage = useMemo(() => {
    if (searchText) {
      return formatMessage(JobMessages.noJobsFoundMessage);
    }
    return formatMessage(JobMessages.noJobsRecordedMessage);
  }, [formatMessage, searchText]);

  // Automatically open the upload modal if routing with upload state.
  useEffect(() => {
    if (locationState?.action === MobileLogListPageActions.Upload && bleState === BleConnectionState.CONNECTED) {
      history.replace({ state: {} });
      setIsOpen(true);
    }
  }, [locationState?.action, bleState, locationState, history]);

  // Show deleted successful toast if the action occurred.
  useEffect(() => {
    if (locationState?.action === MobileLogListPageActions.DeleteSuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: formatMessage(JobMessages.successfulJobDeletionMessage),
        },
        'success',
      );
    }
  }, [formatMessage, history, locationState, present]);

  useEffect(() => {
    BleService.controller?.listLogFiles?.().then(() => console.log('Fired read log files.'));
  }, [connectedDevice?.deviceId]);

  const jobLogOptions: SelectOption[] = React.useMemo(() => {
    return (
      controllerLogFiles?.map(logFile => {
        return {
          _id: logFile.fileName,
          name: logFile.fileName,
        };
      }) ?? []
    );
  }, [controllerLogFiles]);

  useEffect(() => {
    const query = Roles.isStoneAgeDev(userData?.role) ? {} : { org: userData?.org };

    if (userData?.org) {
      setUsersLoading(true);
      // FIXME: Use socket hook instead to take advantage of caching
      portalRequest('users list', {
        query: query,
        paginate: { sort: { createdAt: -1 }, limit: 1000 },
      })
        .then((users: any) => {
          if (!!users) {
            setUsers(users);
          }
        })
        .finally(() => {
          setUsersLoading(false);
        });
    }
  }, [portalRequest, userData?.org, userData?.role]);

  useIonViewWillEnter(() => {
    dispatch(appBarActions.setTitle(formatMessage(JobMessages.jobsTitle)));
    dispatch(appBarActions.showBack(false));
  }, [dispatch, formatMessage]);

  const canUploadLogFromConnectedController = useMemo(() => {
    // StoneAge dev can upload from any controller.
    if (userData?.role === Roles.roles.STONEAGE_DEV) {
      return true;
    }

    // Any other role can only upload from their org's controller.
    return connectedDevice?.isControllerInOrg;
  }, [connectedDevice?.isControllerInOrg, userData?.role]);

  /**
   * Job Log form submission method.
   * @param logFileName The job log file name.
   * @param orgId The unique identifier of the organization the job was performed for.
   * @param siteName The name of the site where the job was performed.
   * @param description Additional details on the job.
   * @param heatExchangerModelNumber The model number of the heat exchanger the job was performed on.
   */
  const onFormSubmit = React.useCallback(
    async (
      logFileName: string,
      orgId: string,
      siteName: string,
      description: string,
      heatExchangerModelNumber: string,
    ) => {
      setIsOpen(false);

      try {
        if (!serialNumber) throw new Error('Serial number not set');
        if (!BleService.controller?.syncLogFile) throw new Error('controller not connected');

        await BleService.controller.syncLogFile(
          logFileName,
          siteName,
          serialNumber,
          description,
          heatExchangerModelNumber,
        );
      } catch (e) {
        console.error('Failed to submit job log:', e);
      }
    },
    [serialNumber],
  );

  const allLogs = React.useMemo<JobsListInfiniteItem[]>(() => {
    const remoteLogs = [...searchResults.results]
      .sort((logA, logB) => (logA.createdAt < logB.createdAt ? 1 : -1))
      .map(job => {
        const user = users.find(u => job.createdBy === u._id);
        const userName = !user
          ? formatMessage(GenericMessages.unknownPlaceholder)
          : `${user.given_name} ${user.family_name}`;
        const item: JobsListInfiniteItem = {
          title: job.description || job.name,
          status: job.status ?? 'complete',
          routerLink: AppRouteConstants.LogDetails.replace(':id', job._id),
          createdAt: job.createdAt,
          timezone: job.deviceTimezone,
          createdByName: userName,
          chipProps:
            job._id === 'live'
              ? { title: formatMessage(JobMessages.jobLive), type: ChipType.JOB_LIVE }
              : job.status === 'in-progress'
                ? { title: formatMessage(JobMessages.jobInProgress), type: ChipType.JOB_INPROGRESS }
                : { title: formatMessage(JobMessages.jobComplete), type: ChipType.JOB_COMPLETE },
          linesParsed: job.linesParsed,
          filename: job.originalFileName || job.name,
          serialNumber: job.serialNumber,
          isLocal: false,
        };
        return item;
      });

    const localLogs = Object.values(localLogFiles).map(log => {
      const createdByName =
        userData?.givenName && userData?.familyName
          ? `${userData.givenName} ${userData.familyName}`
          : userData?.givenName ?? userData?.familyName ?? formatMessage(GenericMessages.unknownPlaceholder);
      const remoteMatch = searchResults.results.find(remote => remote._id === log.id);

      const item: JobsListInfiniteItem = {
        title: log.metadata?.description || remoteMatch?.description || log.filename.replace(/\.log$/i, ''),
        status: !!log.pendingClose ? 'complete' : 'live',
        routerLink: `/jobs/file/${window.encodeURIComponent(log.filename)}`,
        createdByName,
        createdAt: log.createdAt,
        timezone: remoteMatch?.deviceTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        chipProps: !!log.pendingClose
          ? { title: formatMessage(JobMessages.jobComplete), type: ChipType.JOB_COMPLETE }
          : { title: formatMessage(JobMessages.jobLive), type: ChipType.JOB_LIVE },
        linesParsed: log.raw.split('\n').length,
        filename: log.filename,
        serialNumber: log.serialNumber,
        isLocal: true,
      };
      return item;
    });

    const result: JobsListInfiniteItem[] = [...localLogs];
    for (const remote of remoteLogs) {
      if (
        remote.status === 'in-progress' &&
        localLogs.some(l => l.filename === remote.filename && l.serialNumber === remote.serialNumber)
      ) {
        // Remove from the list any remote, in-progress logs that match local logs (otherwise they will display twice)
        continue;
      }
      result.push(remote);
    }
    return result;
  }, [formatMessage, localLogFiles, searchResults.results, userData?.familyName, userData?.givenName, users]);

  // Fetch API data for the connected Controller for user with the upload form.
  const controllerQuery = React.useMemo(() => {
    return { query: { org: userData?.org, ssn: connectedDevice?.serialNumber } };
  }, [userData?.org, connectedDevice?.serialNumber]);
  const { items: controllers } = useSocketList(SocketContextSentinel, 'controllers', controllerQuery);
  const connectedCtrlInfo = controllers?.find(c => c.ssn === connectedDevice?.serialNumber);

  return (
    <IonPage>
      <MobileHeader />
      <IonLoading isOpen={!isLogsFetched || isUsersLoading} spinner="circles" />
      <IonContent scrollX={false} scrollY={false}>
        <div className="jobs-list-content">
          <div className="jobs-list-fixed page-content">
            <FeatureGate featureName={'BUNDLES'}>
              <MobileLogsBundlesTabs />
            </FeatureGate>
            {!isLimited && (
              <div className="searchbar">
                <IonSearchbar
                  placeholder={formatMessage(GenericMessages.searchPlaceholder)}
                  value={searchText}
                  onIonChange={e => setSearchText(e.detail.value!)}
                  debounce={1500}
                />
                <IonButton onClick={() => setFilterModalOpen(true)} color="dark" fill="clear">
                  <IonIcon icon={filter} />
                </IonButton>
              </div>
            )}

            {filterApplied ? (
              <div className={isLimited ? 'ion-padding-top' : ''}>
                <Swiper
                  freeMode={true}
                  spaceBetween={0}
                  observer={true}
                  className="chip-swiper"
                  modules={[FreeMode, IonicSlides]}
                >
                  {Object.entries(filterOptionState)
                    .filter(([_, filterValue]) => !!filterValue)
                    .map(([filterKey, filterValue]) => {
                      return (
                        <SwiperSlide key={filterKey} className="chip-swiper-slide">
                          <IonChip>
                            {formattedFilterValues(filterKey, filterValue)}
                            {!isLimited && (
                              <IonIcon
                                color="dark"
                                onClick={() => setFilterOptionState({ ...filterOptionState, [filterKey]: null })}
                                icon={close}
                              />
                            )}
                          </IonChip>
                        </SwiperSlide>
                      );
                    })}
                </Swiper>
              </div>
            ) : null}
            <JobSyncSlide />
          </div>
          <div className="jobs-list-scroll">
            {!!allLogs.length && !!users ? (
              <JobsListInfinite
                jobs={allLogs}
                loadedEntireResults={searchResults.totalCount <= supportedPageSize}
                totalCount={allLogs.length - (searchResults.results?.length ?? 0) + searchResults.totalCount ?? 0}
              />
            ) : (
              <div className="search-message">
                <DqText variant="h3">{jobsListMessage}</DqText>
              </div>
            )}
          </div>
        </div>
        {/** FIXME: Move this modal and all associated logic out of this component. */}
        <IonModal isOpen={isOpen} initialBreakpoint={0.9} breakpoints={[0, 0.9]} onDidDismiss={() => setIsOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                <FormattedMessage {...JobMessages.uploadJobModalHeader} />
              </IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setIsOpen(false)}>
                  <FormattedMessage {...GenericMessages.closeButtonPlaceholder} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <JobUploadForm
              logFiles={jobLogOptions}
              submitForm={onFormSubmit}
              controllerName={
                connectedCtrlInfo?.physicalSerial
                  ? formatMessage(ControllerMessages.controllerIdLabel, {
                      physicalId: connectedCtrlInfo?.physicalSerial,
                    })
                  : connectedDevice?.serialNumber
              }
            />
          </IonContent>
        </IonModal>
        <FilterJobsModal
          organizations={
            searchResults?.relations?.organizations ? Object.values(searchResults.relations.organizations) : []
          }
          controllerPhysicalSerials={
            searchResults?.relations?.physicalSerials ? Object.values(searchResults.relations.physicalSerials) : []
          }
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onSave={(e: FilterJobsModalState) => applyFilter(e)}
        />

        <IonPopover trigger="cannot-upload-log-icon" triggerAction="click" side="left" dismissOnSelect={true}>
          <IonContent class="ion-padding">{formatMessage(JobMessages.cannotUploadJobLogMessage)}</IonContent>
        </IonPopover>
      </IonContent>
      {hasBluetooth && (
        <IonFooter>
          <PermissionGuard permission="UploadJobLog">
            <IonToolbar>
              <IonRow>
                <IonCol>
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={() => setIsOpen(true)}
                    disabled={bleState !== BleConnectionState.CONNECTED || !canUploadLogFromConnectedController}
                  >
                    <FormattedMessage {...JobMessages.uploadJobButtonLabel} />
                  </IonButton>
                </IonCol>
                {bleState === BleConnectionState.CONNECTED && !canUploadLogFromConnectedController && (
                  <IonCol size="auto" className="ion-align-self-center">
                    <IonIcon id="cannot-upload-log-icon" size="large" icon={warning} color="primary" />
                  </IonCol>
                )}
              </IonRow>
            </IonToolbar>
          </PermissionGuard>
        </IonFooter>
      )}
    </IonPage>
  );
};

export default MobileLogsListPage;
